class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    this.endDate = this.getAttribute("data-date");
    // Two breakpoint-aware types. If `data-type-mobile` isn't set we just
    // fall back to the desktop type for both viewports (back-compat with
    // existing instances that predate the responsive setting).
    this.typeDesktop = this.getAttribute("data-type") || "default";
    this.typeMobile =
      this.getAttribute("data-type-mobile") || this.typeDesktop;
    // 749px is the theme's mobile breakpoint (matches the kc-timer
    // stylesheet @media query). Using matchMedia avoids re-checking
    // window.innerWidth on every tick.
    this.mobileMQ = window.matchMedia("(max-width: 749px)");
    this.type = this.mobileMQ.matches ? this.typeMobile : this.typeDesktop;
    this.showDays = this.getAttribute("data-show-days") === "true";
    this.showHours = this.getAttribute("data-show-hours") === "true";
    this.showMinutes = this.getAttribute("data-show-minutes") === "true";
    this.showSeconds = this.getAttribute("data-show-seconds") === "true";
    this.textDay = (this.getAttribute("data-text-day") || "Days").split(",");
    this.textHour = (this.getAttribute("data-text-hour") || "Hours").split(",");
    this.textMinute = (
      this.getAttribute("data-text-minute") || "Minutes"
    ).split(",");
    this.textSecond = (
      this.getAttribute("data-text-second") || "Seconds"
    ).split(",");
    this.interval = null;

    if (!this.endDate) {
      this.innerHTML = "No end date specified";
      return;
    }

    // Re-render on viewport change so rotating the device or resizing
    // the browser swaps between compact/default cleanly. Only fires
    // when the breakpoint is actually crossed.
    this._mqListener = (e) => {
      const newType = e.matches ? this.typeMobile : this.typeDesktop;
      if (newType !== this.type) {
        this.type = newType;
        this.updateCountdown();
      }
    };
    if (this.mobileMQ.addEventListener) {
      this.mobileMQ.addEventListener("change", this._mqListener);
    } else if (this.mobileMQ.addListener) {
      // Safari < 14 fallback
      this.mobileMQ.addListener(this._mqListener);
    }

    this.startCountdown();
  }

  startCountdown() {
    this.updateCountdown();

    this.interval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const endTime = new Date(this.endDate).getTime();
    const timeLeft = endTime - now;

    if (timeLeft <= 0) {
      this.innerHTML = this.formatExpiredMessage();
      this.classList.add("countdown-expired");
      if (this.interval) {
        clearInterval(this.interval);
      }
      return;
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    if (this.type === "short") {
      this.innerHTML = this.formatShortCountdown(days, hours, minutes, seconds);
    } else if (this.type === "compact") {
      this.innerHTML = this.formatCompactCountdown(
        days,
        hours,
        minutes,
        seconds
      );
    } else {
      this.innerHTML = this.formatCountdown(days, hours, minutes, seconds);
    }
  }

  // "3T 9S 30M": first letter of the label glued to the value, no zero
  // padding, so the same labels serve the full-word styles. Same
  // visibility rules as the compact style.
  formatShortCountdown(days, hours, minutes, seconds) {
    const parts = [];

    if (this.showDays && days > 0) {
      parts.push(`${days}${this.shortLabel(this.textDay)}`);
    }

    if (this.showHours && (hours > 0 || days > 0)) {
      parts.push(`${hours}${this.shortLabel(this.textHour)}`);
    }

    if (this.showMinutes && (minutes > 0 || hours > 0 || days > 0)) {
      parts.push(`${minutes}${this.shortLabel(this.textMinute)}`);
    }

    if (this.showSeconds) {
      parts.push(`${seconds}${this.shortLabel(this.textSecond)}`);
    }

    return `<div class="countdown-container countdown-container--short">${parts.join(" ")}</div>`;
  }

  shortLabel(text) {
    return (text[0] || "").trim().charAt(0).toUpperCase();
  }

  formatCompactCountdown(days, hours, minutes, seconds) {
    const parts = [];

    if (this.showDays && days > 0) {
      const dayText =
        days === 1 ? this.textDay[0] : this.textDay[1] || this.textDay[0];
      parts.push(`${days} ${dayText}`);
    }

    if (this.showHours && (hours > 0 || days > 0)) {
      const hourText =
        hours === 1 ? this.textHour[0] : this.textHour[1] || this.textHour[0];
      parts.push(`${hours.toString().padStart(2, "0")} ${hourText}`);
    }

    if (this.showMinutes && (minutes > 0 || hours > 0 || days > 0)) {
      const minuteText =
        minutes === 1
          ? this.textMinute[0]
          : this.textMinute[1] || this.textMinute[0];
      parts.push(`${minutes.toString().padStart(2, "0")} ${minuteText}`);
    }

    if (this.showSeconds) {
      const secondText =
        seconds === 1
          ? this.textSecond[0]
          : this.textSecond[1] || this.textSecond[0];
      parts.push(`${seconds.toString().padStart(2, "0")} ${secondText}`);
    }

    return `<div class="countdown-container">${parts.join(" ")}</div>`;
  }

  formatCountdown(days, hours, minutes, seconds) {
    const timeUnits = [];

    if (this.showDays && days > 0) {
      timeUnits.push({
        value: days,
        label:
          days === 1 ? this.textDay[0] : this.textDay[1] || this.textDay[0],
      });
    }

    if (this.showHours && (hours > 0 || days > 0)) {
      timeUnits.push({
        value: hours.toString().padStart(2, "0"),
        label:
          hours === 1 ? this.textHour[0] : this.textHour[1] || this.textHour[0],
      });
    }

    if (this.showMinutes && (minutes > 0 || hours > 0 || days > 0)) {
      timeUnits.push({
        value: minutes.toString().padStart(2, "0"),
        label:
          minutes === 1
            ? this.textMinute[0]
            : this.textMinute[1] || this.textMinute[0],
      });
    }

    if (this.showSeconds) {
      timeUnits.push({
        value: seconds.toString().padStart(2, "0"),
        label:
          seconds === 1
            ? this.textSecond[0]
            : this.textSecond[1] || this.textSecond[0],
      });
    }

    return this.createStyledCountdown(timeUnits);
  }

  createStyledCountdown(timeUnits) {
    const countdownHTML = timeUnits
      .map(
        (unit) => `
      <div class="countdown-unit">
        <div class="countdown-value">${unit.value}</div>
        <div class="countdown-label">${unit.label}</div>
      </div>
    `
      )
      .join("");

    return `
      <div class="countdown-container">
        ${countdownHTML}
      </div>
    `;
  }

  formatExpiredMessage() {
    return "";
  }

  disconnectedCallback() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.mobileMQ && this._mqListener) {
      if (this.mobileMQ.removeEventListener) {
        this.mobileMQ.removeEventListener("change", this._mqListener);
      } else if (this.mobileMQ.removeListener) {
        this.mobileMQ.removeListener(this._mqListener);
      }
    }
  }
}

if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}
